import React, { useCallback, useState } from "react";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";
import api from "services/api";

/**
 * Expense Trends Report - Monthly spending dynamics and category concentration
 */
export const ExpenseTrends = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  
  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const formatCurrency = useCallback(
    (value) =>
      `$${(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [],
  );

  const formatDate = useCallback((value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/financial/expense-trends", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const openExpenseRowsDrawer = useCallback(
    (title, subtitle, description, rows) => {
      const sortedRows = [...rows].sort((a, b) => (b.amount || 0) - (a.amount || 0));

      setDrillDown({
        open: true,
        title,
        subtitle,
        description,
        data: sortedRows,
        columns: [
          {
            field: "expenseDate",
            headerName: "Expense Date",
            width: 130,
            renderCell: (row) => formatDate(row.expenseDate),
          },
          { field: "category", headerName: "Category", width: 170 },
          { field: "subCategory", headerName: "Sub-Category", width: 180 },
          { field: "paymentMethod", headerName: "Payment Method", width: 150 },
          {
            field: "amount",
            headerName: "Amount",
            width: 130,
            renderCell: (row) => formatCurrency(row.amount),
          },
          {
            field: "deductiblePercentage",
            headerName: "Deductible %",
            width: 110,
            renderCell: (row) => `${(row.deductiblePercentage || 0).toFixed(1)}%`,
          },
          {
            field: "deductibleAmount",
            headerName: "Deductible Amount",
            width: 150,
            renderCell: (row) => formatCurrency(row.deductibleAmount),
          },
          {
            field: "description",
            headerName: "Description",
            width: 300,
          },
        ],
        summary: {
          metrics: [
            { label: "Entries", value: sortedRows.length },
            {
              label: "Total Amount",
              value: formatCurrency(
                sortedRows.reduce((sum, row) => sum + (row.amount || 0), 0),
              ),
            },
            {
              label: "Avg Entry",
              value: formatCurrency(
                sortedRows.length > 0
                  ? sortedRows.reduce((sum, row) => sum + (row.amount || 0), 0) /
                      sortedRows.length
                  : 0,
              ),
            },
          ],
        },
      });
    },
    [formatCurrency, formatDate],
  );

  const openMonthlyDrawer = useCallback(
    (title, subtitle, description, rows) => {
      const sortedRows = [...rows].sort((a, b) => a.monthKey.localeCompare(b.monthKey));
      setDrillDown({
        open: true,
        title,
        subtitle,
        description,
        data: sortedRows,
        columns: [
          { field: "monthLabel", headerName: "Month", width: 130 },
          {
            field: "amount",
            headerName: "Total Spend",
            width: 140,
            renderCell: (row) => formatCurrency(row.amount),
          },
          { field: "count", headerName: "Entries", width: 110 },
          {
            field: "avgExpense",
            headerName: "Avg Entry",
            width: 130,
            renderCell: (row) => formatCurrency(row.avgExpense),
          },
          {
            field: "deductibleAmount",
            headerName: "Deductible",
            width: 130,
            renderCell: (row) => formatCurrency(row.deductibleAmount),
          },
        ],
        summary: {
          metrics: [
            { label: "Months", value: sortedRows.length },
            {
              label: "Total Spend",
              value: formatCurrency(
                sortedRows.reduce((sum, row) => sum + (row.amount || 0), 0),
              ),
            },
            {
              label: "Avg Monthly",
              value: formatCurrency(
                sortedRows.length > 0
                  ? sortedRows.reduce((sum, row) => sum + (row.amount || 0), 0) /
                      sortedRows.length
                  : 0,
              ),
            },
          ],
        },
      });
    },
    [formatCurrency],
  );

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const monthRows = data.monthlyTrend?.rows || [];
      const expenseRows = data.expenseRows || [];
      const topCategory = summary.topCategory;
      const topSubCategory = (data.subCategoryBreakdown || [])[0] || null;
      const momPositive = (summary.momChangePercent || 0) >= 0;

      return [
        {
          groupTitle: "Spend Overview",
          metrics: [
            {
              title: "Total Expenses",
              value: formatCurrency(summary.totalExpenses || 0),
              subtitle: `${summary.expenseCount || 0} expense entries`,
              color: "warning.main",
              tooltipTitle: "Total Expenses",
              tooltipContent:
                "Definition: Sum of all expense amounts in the selected period.\n\nThis is the core spend baseline used to evaluate trend direction and concentration risk.\n\nClick to open the full expense ledger behind this value.",
              onClick: () =>
                openExpenseRowsDrawer(
                  "All Expenses",
                  `${expenseRows.length} entries • ${formatCurrency(summary.totalExpenses || 0)}`,
                  "Complete expense ledger for the selected period.",
                  expenseRows,
                ),
            },
            {
              title: "Avg Monthly Spend",
              value: formatCurrency(summary.avgMonthlySpend || 0),
              subtitle: `${summary.monthsCovered || 0} active months`,
              tooltipTitle: "Average Monthly Spend",
              tooltipContent:
                "Definition: Total Expenses divided by number of months with expense activity.\n\nUse this to normalize spending and compare periods with different lengths.\n\nClick to view monthly breakdown rows.",
              onClick: () =>
                openMonthlyDrawer(
                  "Monthly Expense Breakdown",
                  `${monthRows.length} months • ${formatCurrency(summary.totalExpenses || 0)}`,
                  "Monthly spend, volume, average ticket, and deductible totals.",
                  monthRows,
                ),
            },
            {
              title: "Expense Entries",
              value: `${summary.expenseCount || 0}`,
              subtitle: `${formatCurrency(summary.avgExpenseAmount || 0)} avg entry`,
              tooltipTitle: "Expense Entries",
              tooltipContent:
                "Definition: Number of individual expense records in scope.\n\nPaired with Avg Entry, this shows whether spend changes are volume-driven or ticket-size-driven.\n\nClick to review all expense rows.",
              onClick: () =>
                openExpenseRowsDrawer(
                  "Expense Entries",
                  `${expenseRows.length} entries`,
                  "All expense entries sorted by amount.",
                  expenseRows,
                ),
            },
            {
              title: "Top Category",
              value: topCategory?.category || "-",
              subtitle: formatCurrency(topCategory?.amount || 0),
              color: "info.main",
              tooltipTitle: "Top Expense Category",
              tooltipContent:
                "Definition: Category with the highest total spend in the selected period.\n\nThis identifies where most budget pressure is concentrated.\n\nClick to open only entries from this category.",
              onClick: () => {
                const rows = expenseRows.filter(
                  (row) => row.category === topCategory?.category,
                );
                openExpenseRowsDrawer(
                  `Top Category - ${topCategory?.category || "-"}`,
                  `${rows.length} entries • ${formatCurrency(topCategory?.amount || 0)}`,
                  "Expenses within the highest-spend category.",
                  rows,
                );
              },
            },
            {
              title: "Top Sub-Category",
              value: topSubCategory
                ? `${topSubCategory.category} • ${topSubCategory.subCategory}`
                : "-",
              subtitle: formatCurrency(topSubCategory?.amount || 0),
              color: "secondary.main",
              tooltipTitle: "Top Expense Sub-Category",
              tooltipContent:
                "Definition: Sub-category with the highest total spend in the selected period.\n\nThis helps identify the most specific cost driver within a broader category.\n\nClick to open only entries from this sub-category.",
              onClick: () => {
                const rows = expenseRows.filter(
                  (row) =>
                    row.category === topSubCategory?.category &&
                    row.subCategory === topSubCategory?.subCategory,
                );
                openExpenseRowsDrawer(
                  topSubCategory
                    ? `Top Sub-Category - ${topSubCategory.category} • ${topSubCategory.subCategory}`
                    : "Top Sub-Category",
                  `${rows.length} entries • ${formatCurrency(topSubCategory?.amount || 0)}`,
                  "Expenses within the highest-spend sub-category.",
                  rows,
                );
              },
            },
          ],
        },
        {
          groupTitle: "Trend Signals",
          metrics: [
            {
              title: "MoM Spend Change",
              value: `${momPositive ? "+" : ""}${(summary.momChangePercent || 0).toFixed(2)}%`,
              subtitle: `${summary.previousMonthLabel || "-"} → ${summary.latestMonthLabel || "-"}`,
              color: momPositive ? "warning.main" : "success.main",
              tooltipTitle: "Month-over-Month Spend Change",
              tooltipContent:
                "Definition: Percent change in total spend from previous month to latest month.\n\nPositive indicates spend increased; negative indicates spend decreased.\n\nUse with monthly trend chart to verify if change is one-off or persistent.",
            },
            {
              title: "Deductible Amount",
              value: formatCurrency(summary.totalDeductible || 0),
              subtitle: `${(summary.deductibleRate || 0).toFixed(2)}% of total spend`,
              tooltipTitle: "Deductible Amount",
              tooltipContent:
                "Definition: Expense amount eligible for deduction based on each entry's deductible percentage.\n\nHelps estimate tax-impacting spend share.\n\nCross-check with category and subcategory to improve classification quality.",
            },
            {
              title: "Highest Month",
              value: summary.highestMonth?.monthLabel || "-",
              subtitle: formatCurrency(summary.highestMonth?.amount || 0),
              color: "warning.main",
              tooltipTitle: "Highest Spend Month",
              tooltipContent:
                "Definition: Month with the maximum total expense amount.\n\nUse this to investigate peak-spend drivers and recurrence patterns.\n\nClick to open month-specific expenses.",
              onClick: () => {
                const rows = expenseRows.filter(
                  (row) => row.monthKey === summary.highestMonth?.monthKey,
                );
                openExpenseRowsDrawer(
                  `Highest Month - ${summary.highestMonth?.monthLabel || "-"}`,
                  `${rows.length} entries • ${formatCurrency(summary.highestMonth?.amount || 0)}`,
                  "Expense entries from the highest-spend month.",
                  rows,
                );
              },
            },
            {
              title: "Lowest Month",
              value: summary.lowestMonth?.monthLabel || "-",
              subtitle: formatCurrency(summary.lowestMonth?.amount || 0),
              color: "success.main",
              tooltipTitle: "Lowest Spend Month",
              tooltipContent:
                "Definition: Month with the minimum total expense amount.\n\nUse as a baseline when reviewing spend stabilization and forecasting.\n\nClick to open month-specific expenses.",
              onClick: () => {
                const rows = expenseRows.filter(
                  (row) => row.monthKey === summary.lowestMonth?.monthKey,
                );
                openExpenseRowsDrawer(
                  `Lowest Month - ${summary.lowestMonth?.monthLabel || "-"}`,
                  `${rows.length} entries • ${formatCurrency(summary.lowestMonth?.amount || 0)}`,
                  "Expense entries from the lowest-spend month.",
                  rows,
                );
              },
            },
          ],
        },
      ];
    },
    [formatCurrency, openExpenseRowsDrawer, openMonthlyDrawer],
  );

  const chartsConfig = useCallback(
    (data) => {
      const monthlySpendRows = Object.values(
        (data.expenseRows || []).reduce((acc, row) => {
          if (!row?.monthKey || row.monthKey === "Unknown") return acc;

          if (!acc[row.monthKey]) {
            acc[row.monthKey] = {
              monthKey: row.monthKey,
              monthLabel: row.monthLabel,
              totalSpend: 0,
              count: 0,
            };
          }

          acc[row.monthKey].totalSpend += row.amount || 0;
          acc[row.monthKey].count += 1;
          return acc;
        }, {}),
      )
        .sort((a, b) => a.monthKey.localeCompare(b.monthKey))
        .map((row) => ({
          ...row,
          avgExpense: row.count > 0 ? row.totalSpend / row.count : 0,
        }));

      const categoryData = (data.categoryBreakdown || []).map((category, idx) => ({
        id: idx + 1,
        label: `${category.category} (${formatCurrency(category.amount)})`,
        value: category.amount,
      }));
      const paymentMethodData = (data.paymentMethodBreakdown || []).map((method) => ({
        id: method.id,
        label: `${method.paymentMethod} (${method.count})`,
        value: method.amount,
      }));

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Monthly Expense Spend"
              data={monthlySpendRows}
              xAxisKey="monthLabel"
              series={[
                {
                  dataKey: "totalSpend",
                  label: "Expense Amount",
                  color: chartColors.warning,
                },
              ]}
              height={350}
              yAxisLabel="Amount ($)"
              yAxisScaleType="linear"
              valueFormatter={(value) => formatCurrency(value)}
              onBarClick={(item) => {
                const rows = (data.expenseRows || []).filter(
                  (row) => row.monthKey === item.monthKey,
                );
                openExpenseRowsDrawer(
                  `Expenses - ${item.monthLabel}`,
                  `${rows.length} entries • ${formatCurrency(item.totalSpend || 0)}`,
                  "All expenses posted in the selected month.",
                  rows,
                );
              }}
              tooltipTitle="Monthly Expense Spend"
              tooltipContent={
                "Shows total expense amount by month.\n\nHow to read:\n- Rising bars indicate spend acceleration.\n- Large spikes often indicate one-time items or seasonal costs.\n\nClick a bar to open month-level expense entries."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportLineChart
              title="Expense Volume & Avg Entry"
              xAxisData={monthlySpendRows.map((m) => m.monthLabel)}
              series={[
                {
                  data: monthlySpendRows.map((m) => m.count),
                  label: "Entries",
                  curve: "monotoneX",
                  color: chartColors.info,
                },
                {
                  data: monthlySpendRows.map((m) => m.avgExpense),
                  label: "Avg Entry ($)",
                  curve: "monotoneX",
                  color: chartColors.semantic.primary,
                },
              ]}
              height={350}
              valueFormatter={(value) => (value || 0).toLocaleString()}
              tooltipTitle="Expense Volume vs Ticket Size"
              tooltipContent={
                "Compares monthly entry count against average expense per entry.\n\nUse this to distinguish if spend growth is coming from more transactions or larger transactions.\n\nA rising average with flat count indicates ticket-size inflation."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Expense Distribution by Category"
              data={categoryData}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => formatCurrency(value)}
              onSliceClick={(item) => {
                const categoryName = String(item.label || "").replace(
                  /\s*\([^)]*\)\s*$/,
                  "",
                );
                const rows = (data.expenseRows || []).filter(
                  (row) => row.category === categoryName,
                );
                openExpenseRowsDrawer(
                  `Category - ${categoryName}`,
                  `${rows.length} entries • ${formatCurrency(
                    rows.reduce((sum, row) => sum + (row.amount || 0), 0),
                  )}`,
                  "Expenses for the selected category.",
                  rows,
                );
              }}
              tooltipTitle="Category Concentration"
              tooltipContent={
                "Shows where total spend is concentrated across categories.\n\nHigher concentration in one category may indicate budget dependency risk.\n\nClick a slice to view expense entries for that category."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportBarChart
              title="Top Sub-Categories by Spend"
              data={(data.subCategoryBreakdown || []).slice(0, 8).map((item) => ({
                ...item,
                label: `${item.category} • ${item.subCategory}`,
              }))}
              xAxisKey="label"
              series={[
                {
                  dataKey: "amount",
                  label: "Amount",
                  color: chartColors.error,
                },
              ]}
              height={350}
              yAxisLabel="Amount ($)"
              yAxisScaleType="linear"
              valueFormatter={(value) => formatCurrency(value)}
              onBarClick={(item) => {
                const rows = (data.expenseRows || []).filter(
                  (row) =>
                    row.category === item.category && row.subCategory === item.subCategory,
                );
                openExpenseRowsDrawer(
                  `Sub-Category - ${item.category} • ${item.subCategory}`,
                  `${rows.length} entries • ${formatCurrency(item.amount || 0)}`,
                  "Expenses for the selected category and sub-category.",
                  rows,
                );
              }}
              tooltipTitle="Sub-Category Detail"
              tooltipContent={
                "Ranks highest-spend sub-categories to show granular cost drivers.\n\nUse this for targeted budget controls and category cleanup.\n\nClick a bar to view underlying entries."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Expense Mix by Payment Method"
              data={paymentMethodData}
              height={350}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              valueFormatter={(value) => formatCurrency(value)}
              tooltipTitle="Payment Method Mix"
              tooltipContent={
                "Shows total spend distribution by payment method.\n\nUse this to monitor payment-channel concentration and support treasury controls.\n\nPairs well with policy checks for card/check/wire usage."
              }
            />
          </Grid>
        </Grid>
      );
    },
    [
      chartColors.warning,
      chartColors.info,
      chartColors.semantic.primary,
      chartColors.error,
      formatCurrency,
      openExpenseRowsDrawer,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Expense Trends"
        description="Business expense trend analysis with monthly dynamics, concentration signals, and drill-down visibility into expense drivers."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Profit & Loss Statement - Revenue, cost, and gross profitability summary
 */
export const ProfitLossStatement = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = () => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  };

  const formatCurrency = useCallback(
    (value) =>
      `$${(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [],
  );

  const formatPercent = useCallback((value) => `${(value || 0).toFixed(2)}%`, []);

  const dataFetchFn = useCallback(async (activeFilters) => {
    const response = await api.get("/reports/financial/profit-margin", {
      params: activeFilters,
    });
    return response.data;
  }, []);

  const buildMonthlyRows = useCallback((data) => {
    const months = data.monthlyData?.months || [];
    return months.map((month, idx) => ({
      id: `${month}-${idx}`,
      month,
      revenue: data.monthlyData?.revenue?.[idx] || 0,
      costs: data.monthlyData?.costs?.[idx] || 0,
      grossProfit: data.monthlyData?.profit?.[idx] || 0,
      grossMargin: data.monthlyData?.margin?.[idx] || 0,
      servicesCount: data.monthlyData?.servicesCount?.[idx] || 0,
    }));
  }, []);

  const openMonthlyDrawer = useCallback(
    (data, title) => {
      const monthlyRows = buildMonthlyRows(data);
      setDrillDown({
        open: true,
        title,
        subtitle: `${monthlyRows.length} months • ${formatCurrency(data.totalRevenue || 0)} revenue`,
        description: "Month-by-month performance for revenue, cost of revenue, and gross profit.",
        data: monthlyRows,
        columns: [
          { field: "month", headerName: "Month", width: 120 },
          {
            field: "revenue",
            headerName: "Revenue",
            width: 130,
            renderCell: (row) => formatCurrency(row.revenue),
          },
          {
            field: "costs",
            headerName: "Cost of Revenue",
            width: 150,
            renderCell: (row) => formatCurrency(row.costs),
          },
          {
            field: "grossProfit",
            headerName: "Gross Profit",
            width: 130,
            renderCell: (row) => formatCurrency(row.grossProfit),
          },
          {
            field: "grossMargin",
            headerName: "Gross Margin",
            width: 130,
            renderCell: (row) => formatPercent(row.grossMargin),
          },
          { field: "servicesCount", headerName: "Services", width: 100 },
        ],
        summary: {
          metrics: [
            { label: "Total Revenue", value: formatCurrency(data.totalRevenue || 0) },
            { label: "Total Cost", value: formatCurrency(data.costOfRevenue || data.totalCosts || 0) },
            {
              label: "Gross Profit",
              value: formatCurrency(data.totalProfit || 0),
              color: (data.totalProfit || 0) >= 0 ? "success.main" : "error.main",
            },
            {
              label: "Gross Margin",
              value: formatPercent(data.avgProfitMargin || 0),
              color: (data.avgProfitMargin || 0) >= 0 ? "success.main" : "error.main",
            },
          ],
        },
      });
    },
    [buildMonthlyRows, formatCurrency, formatPercent],
  );

  const openMarginBandsDrawer = useCallback(
    (data) => {
      const rows = (data.marginBandData || []).map((band, idx) => ({
        id: idx + 1,
        ...band,
      }));
      setDrillDown({
        open: true,
        title: "Profitability Quality by Margin Band",
        subtitle: `${rows.reduce((sum, row) => sum + (row.servicesCount || 0), 0)} services analyzed`,
        description: "Distribution of services across profitability bands to identify where margin quality is concentrated.",
        data: rows,
        columns: [
          { field: "band", headerName: "Margin Band", width: 180 },
          { field: "servicesCount", headerName: "Services", width: 100 },
          {
            field: "revenue",
            headerName: "Revenue",
            width: 130,
            renderCell: (row) => formatCurrency(row.revenue),
          },
          {
            field: "costs",
            headerName: "Cost",
            width: 130,
            renderCell: (row) => formatCurrency(row.costs),
          },
          {
            field: "profit",
            headerName: "Profit",
            width: 130,
            renderCell: (row) => formatCurrency(row.profit),
          },
          {
            field: "avgMargin",
            headerName: "Avg Margin",
            width: 120,
            renderCell: (row) => formatPercent(row.avgMargin),
          },
        ],
        summary: {
          metrics: [
            {
              label: "Profitable Services",
              value: `${data.profitabilityMix?.profitableServices || 0}`,
              color: "success.main",
            },
            {
              label: "Loss-Making Services",
              value: `${data.profitabilityMix?.lossMakingServices || 0}`,
              color: "error.main",
            },
            {
              label: "Profitable Rate",
              value: `${(data.profitabilityMix?.profitableRate || 0).toFixed(1)}%`,
              color: "success.main",
            },
          ],
        },
      });
    },
    [formatCurrency, formatPercent],
  );

  const metricsConfig = useCallback(
    (data) => [
      {
        groupTitle: "P&L Snapshot",
        metrics: [
          {
            title: "Total Revenue",
            value: formatCurrency(data.totalRevenue || 0),
            subtitle: `${data.yoyRevenueGrowth >= 0 ? "+" : ""}${formatPercent(data.yoyRevenueGrowth || 0)} YoY`,
            color: "success.main",
            tooltipTitle: "Total Revenue",
            tooltipContent:
              "Definition: Sum of Account Charge across services in the selected period.\n\nThis is the top line of your P&L and the base for all margin calculations.\n\nClick to open monthly revenue-cost-profit detail.",
            onClick: () => openMonthlyDrawer(data, "Revenue Detail"),
          },
          {
            title: "Cost of Revenue",
            value: formatCurrency(data.costOfRevenue || data.totalCosts || 0),
            subtitle: `${data.yoyCostGrowth >= 0 ? "+" : ""}${formatPercent(data.yoyCostGrowth || 0)} YoY`,
            color: "warning.main",
            tooltipTitle: "Cost of Revenue",
            tooltipContent:
              "Definition: Direct service delivery costs tied to fulfilling revenue (linguist pay and direct service costs).\n\nThis determines how much gross profit is retained from each revenue dollar.\n\nClick to inspect monthly movement.",
            onClick: () => openMonthlyDrawer(data, "Cost Detail"),
          },
          {
            title: "Gross Profit",
            value: formatCurrency(data.totalProfit || 0),
            subtitle: `${data.yoyGrossProfitGrowth >= 0 ? "+" : ""}${formatPercent(data.yoyGrossProfitGrowth || 0)} YoY`,
            color: (data.totalProfit || 0) >= 0 ? "success.main" : "error.main",
            tooltipTitle: "Gross Profit",
            tooltipContent:
              "Definition: Total Revenue minus Cost of Revenue.\n\nThis is your operational profit before overhead and non-service operating expenses.\n\nClick to open monthly gross profit detail.",
            onClick: () => openMonthlyDrawer(data, "Gross Profit Detail"),
          },
          {
            title: "Gross Margin",
            value: formatPercent(data.avgProfitMargin || 0),
            subtitle: `${data.momGrossProfitGrowth >= 0 ? "+" : ""}${formatPercent(data.momGrossProfitGrowth || 0)} MoM`,
            color:
              (data.avgProfitMargin || 0) >= 35
                ? "success.main"
                : (data.avgProfitMargin || 0) >= 20
                  ? "warning.main"
                  : "error.main",
            tooltipTitle: "Gross Margin %",
            tooltipContent:
              "Definition: Gross Profit divided by Total Revenue.\n\nShows how efficiently revenue is converted into gross profit after direct costs.\n\nHigher margin indicates stronger pricing-cost structure.",
          },
        ],
      },
      {
        groupTitle: "Profitability Quality",
        metrics: [
          {
            title: "Profitable Services",
            value: `${data.profitabilityMix?.profitableServices || 0}`,
            subtitle: `${(data.profitabilityMix?.profitableRate || 0).toFixed(1)}% of services`,
            color: "success.main",
            tooltipTitle: "Profitable Services",
            tooltipContent:
              "Definition: Count of services where revenue exceeds direct cost.\n\nThis indicates how consistently service delivery creates positive gross contribution.\n\nClick to see distribution by margin bands.",
            onClick: () => openMarginBandsDrawer(data),
          },
          {
            title: "Loss-Making Services",
            value: `${data.profitabilityMix?.lossMakingServices || 0}`,
            subtitle: `${(data.profitabilityMix?.lossRate || 0).toFixed(1)}% of services`,
            color: "error.main",
            tooltipTitle: "Loss-Making Services",
            tooltipContent:
              "Definition: Count of services where direct cost exceeds billed revenue.\n\nUseful for identifying pricing leakage and high-cost service patterns.\n\nClick to inspect margin bands for concentration.",
            onClick: () => openMarginBandsDrawer(data),
          },
        ],
      },
    ],
    [formatCurrency, formatPercent, openMarginBandsDrawer, openMonthlyDrawer],
  );

  const chartsConfig = useCallback(
    (data) => {
      const monthlyRows = buildMonthlyRows(data);
      const profitabilityMixData = [
        {
          id: 1,
          label: `Profitable (${data.profitabilityMix?.profitableServices || 0})`,
          value: data.profitabilityMix?.profitableServices || 0,
        },
        {
          id: 2,
          label: `Loss-Making (${data.profitabilityMix?.lossMakingServices || 0})`,
          value: data.profitabilityMix?.lossMakingServices || 0,
        },
        {
          id: 3,
          label: `Break-Even (${data.profitabilityMix?.breakEvenServices || 0})`,
          value: data.profitabilityMix?.breakEvenServices || 0,
        },
      ].filter((item) => item.value > 0);

      const topClientsByMargin = (data.topClientsByMargin || []).slice(0, 8).map((client) => ({
        ...client,
        label: client.clientName,
      }));

      return (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <ReportBarChart
              title="Monthly P&L Performance"
              data={monthlyRows}
              xAxisKey="month"
              series={[
                { dataKey: "revenue", label: "Revenue", color: chartColors.semantic.primary },
                { dataKey: "costs", label: "Cost of Revenue", color: chartColors.warning },
                { dataKey: "grossProfit", label: "Gross Profit", color: chartColors.success },
              ]}
              height={360}
              yAxisLabel="Amount ($)"
              yAxisScaleType="linear"
              valueFormatter={(value) => formatCurrency(value)}
              onBarClick={() => openMonthlyDrawer(data, "Monthly P&L Detail")}
              tooltipTitle="Monthly P&L"
              tooltipContent={
                "Compares monthly revenue, direct costs, and gross profit side-by-side.\n\nUse this to identify margin compression months (cost bars rising faster than revenue).\n\nClick to open monthly detail table."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportLineChart
              title="Gross Margin Trend"
              xAxisData={monthlyRows.map((row) => row.month)}
              series={[
                {
                  data: monthlyRows.map((row) => row.grossMargin),
                  label: "Gross Margin %",
                  curve: "monotoneX",
                  color: chartColors.info,
                },
              ]}
              height={320}
              yAxisLabel="Margin (%)"
              valueFormatter={(value) => `${(value || 0).toFixed(2)}%`}
              tooltipTitle="Gross Margin Trend"
              tooltipContent={
                "Shows monthly gross margin percentage.\n\nUpward trend suggests better pricing-cost efficiency; downward trend suggests margin pressure."
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ReportPieChart
              title="Service Profitability Mix"
              data={profitabilityMixData}
              height={320}
              showLabels={false}
              showLegend={true}
              legendPosition="right"
              tooltipTitle="Profitability Mix"
              tooltipContent={
                "Shows share of profitable, loss-making, and break-even services.\n\nClick a KPI to inspect margin-band detail for root-cause analysis."
              }
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <ReportBarChart
              title="Top Clients by Profit Margin"
              data={topClientsByMargin}
              xAxisKey="label"
              series={[
                {
                  dataKey: "profitMargin",
                  label: "Profit Margin %",
                  color: chartColors.success,
                },
              ]}
              height={340}
              showLegend={false}
              yAxisLabel="Margin (%)"
              yAxisScaleType="linear"
              valueFormatter={(value) => `${(value || 0).toFixed(2)}%`}
              tooltipTitle="Top Client Margin"
              tooltipContent={
                "Ranks clients by gross margin contribution quality.\n\nUse with revenue size to prioritize accounts that are both large and efficient."
              }
            />
          </Grid>
        </Grid>
      );
    },
    [
      buildMonthlyRows,
      chartColors.semantic.primary,
      chartColors.warning,
      chartColors.success,
      chartColors.info,
      formatCurrency,
      openMonthlyDrawer,
    ],
  );

  return (
    <>
      <DashboardReport
        title="Profit & Loss Statement"
        description="Comprehensive P&L view across revenue, cost of revenue, gross profit, margin quality, and client-level profitability signals."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

/**
 * Expense Summary & Allocation Report - category allocation + payment method distribution
 */
export const ExpenseSummaryAllocation = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);

  const [drillDown, setDrillDown] = useState({
    open: false,
    title: "",
    subtitle: "",
    description: "",
    data: [],
    columns: [],
    summary: null,
  });

  const closeDrillDown = useCallback(() => {
    setDrillDown({
      open: false,
      title: "",
      subtitle: "",
      description: "",
      data: [],
      columns: [],
      summary: null,
    });
  }, []);

  const formatCurrency = useCallback(
    (value) =>
      `$${(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`,
    [],
  );

  const dataFetchFn = useCallback(async (activeFilters) => {
    const [summaryResponse, trendsResponse, byCategoryResponse] =
      await Promise.all([
        api.get("/reports/expense/summary", { params: activeFilters }),
        api.get("/reports/financial/expense-trends", { params: activeFilters }),
        api.get("/reports/expense/by-category", { params: activeFilters }),
      ]);

    const summary = summaryResponse.data?.data || {};
    const trends = trendsResponse.data?.data || {};
    const byCategory = byCategoryResponse.data?.data || {};

    return {
      data: {
        summary,
        trends,
        byCategory,
      },
    };
  }, []);

  const openDrawer = useCallback((title, subtitle, description, rows, columns) => {
    setDrillDown({
      open: true,
      title,
      subtitle,
      description,
      data: rows,
      columns,
    });
  }, []);

  const metricsConfig = useCallback(
    (data) => {
      const summary = data.summary || {};
      const categoryRows = summary.tableData || [];
      const trendsSummary = data.trends?.summary || {};
      const paymentRows = data.trends?.paymentMethodBreakdown || [];

      return [
        {
          groupTitle: "Expense Totals",
          metrics: [
            {
              title: "Total Expenses",
              value: formatCurrency(summary.totalExpenses || 0),
              subtitle: `${summary.expenseCount || 0} entries`,
              tooltipTitle: "Total Expenses",
              tooltipContent: `Aggregate expense outflow recorded in the selected scope.

Current period context:
- Total Expenses: ${formatCurrency(summary.totalExpenses || 0)}
- Expense Entries: ${summary.expenseCount || 0}

Use this as the baseline spend measure before evaluating category concentration and deductibility quality.`,
              color: "warning.main",
              onClick: () =>
                openDrawer(
                  "Expense Allocation by Category",
                  `${categoryRows.length} categories`,
                  "Category-level allocation based on expense totals for selected filters.",
                  categoryRows,
                  [
                    { field: "category", headerName: "Category", width: 220 },
                    {
                      field: "amount",
                      headerName: "Amount",
                      width: 140,
                      renderCell: (row) => formatCurrency(row.amount),
                    },
                    { field: "count", headerName: "Entries", width: 100 },
                  ],
                ),
            },
            {
              title: "Deductible Expenses",
              value: formatCurrency(summary.deductibleExpenses || 0),
              subtitle:
                (summary.totalExpenses || 0) > 0
                  ? `${((summary.deductibleExpenses / summary.totalExpenses) * 100).toFixed(1)}% deductible`
                  : "0.0% deductible",
              tooltipTitle: "Deductible Expenses",
              tooltipContent: `Portion of spend classified as tax-deductible.

Current period context:
- Deductible Amount: ${formatCurrency(summary.deductibleExpenses || 0)}
- Deductible Share: ${(summary.totalExpenses || 0) > 0
                  ? ((summary.deductibleExpenses / summary.totalExpenses) * 100).toFixed(1)
                  : "0.0"}%

Use this to monitor tax-efficiency posture and identify categories where documentation or coding can improve deductibility outcomes.`,
              onClick: () =>
                openDrawer(
                  "Subcategory Deductible Allocation",
                  `${(data.byCategory?.tableData || []).length} rows`,
                  "Sub-category level allocation with deductible split.",
                  data.byCategory?.tableData || [],
                  [
                    { field: "category", headerName: "Category", width: 180 },
                    { field: "subCategory", headerName: "Sub Category", width: 200 },
                    {
                      field: "amount",
                      headerName: "Amount",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.amount),
                    },
                    {
                      field: "deductible",
                      headerName: "Deductible",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.deductible),
                    },
                    {
                      field: "deductiblePercentage",
                      headerName: "Deductible %",
                      width: 120,
                      renderCell: (row) => `${(row.deductiblePercentage || 0).toFixed(1)}%`,
                    },
                  ],
                ),
            },
            {
              title: "Average Monthly Spend",
              value: formatCurrency(trendsSummary.avgMonthlySpend || 0),
              subtitle: `${trendsSummary.monthsCovered || 0} active months`,
              tooltipTitle: "Average Monthly Spend",
              tooltipContent: `Average monthly expense run-rate across active months.

Current period context:
- Avg Monthly Spend: ${formatCurrency(trendsSummary.avgMonthlySpend || 0)}
- Active Months Covered: ${trendsSummary.monthsCovered || 0}

Use this as your planning baseline for budgeting, variance analysis, and cash requirement forecasting.`,
              onClick: () =>
                openDrawer(
                  "Monthly Expense Allocation",
                  `${(data.trends?.monthlyTrend?.rows || []).length} months`,
                  "Monthly total spend, entry count, and average ticket.",
                  data.trends?.monthlyTrend?.rows || [],
                  [
                    { field: "monthLabel", headerName: "Month", width: 130 },
                    {
                      field: "amount",
                      headerName: "Spend",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.amount),
                    },
                    { field: "count", headerName: "Entries", width: 100 },
                    {
                      field: "avgExpense",
                      headerName: "Avg Entry",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.avgExpense),
                    },
                  ],
                ),
            },
            {
              title: "Payment Method Mix",
              value: paymentRows.length,
              subtitle: "Methods with tracked expense volume",
              tooltipTitle: "Payment Method Mix",
              tooltipContent: `Number of payment methods with recorded expense activity.

Current period context:
- Active Payment Methods: ${paymentRows.length}

Interpretation:
- Broader mix can improve flexibility but may increase reconciliation complexity
- Concentrated mix can simplify control but increase rail dependency risk

Drill down to compare amount share and transaction count by method.`,
              color: "info.main",
              onClick: () =>
                openDrawer(
                  "Expense Payment Method Distribution",
                  `${paymentRows.length} payment methods`,
                  "Payment-method mix by amount and entry count.",
                  paymentRows,
                  [
                    { field: "paymentMethod", headerName: "Payment Method", width: 190 },
                    {
                      field: "amount",
                      headerName: "Amount",
                      width: 130,
                      renderCell: (row) => formatCurrency(row.amount),
                    },
                    { field: "count", headerName: "Entries", width: 100 },
                    {
                      field: "sharePercent",
                      headerName: "Share",
                      width: 100,
                      renderCell: (row) => `${(row.sharePercent || 0).toFixed(1)}%`,
                    },
                  ],
                ),
            },
          ],
        },
      ];
    },
    [formatCurrency, openDrawer],
  );

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Expense Allocation by Category"
            tooltipTitle="Expense Allocation by Category"
            tooltipContent="Shows how total spend is distributed by category. Use this to identify concentration risk, detect category drift, and prioritize cost optimization where dollar impact is highest."
            data={(data.summary?.tableData || []).map((row, index) => ({
              id: index + 1,
              label: `${row.category} (${row.count})`,
              value: row.amount || 0,
            }))}
            height={360}
            valueFormatter={formatCurrency}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <ReportPieChart
            title="Expense Payment Methods"
            tooltipTitle="Expense Payment Methods"
            tooltipContent="Displays spend distribution by payment method. Compare this with policy and control design to ensure high-volume rails have strong governance and efficient reconciliation."
            data={(data.trends?.paymentMethodBreakdown || []).map((row) => ({
              id: row.id,
              label: `${row.paymentMethod} (${row.count})`,
              value: row.amount || 0,
            }))}
            height={360}
            valueFormatter={formatCurrency}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <ReportBarChart
            title="Top Sub-Category Allocation"
            tooltipTitle="Top Sub-Category Allocation"
            tooltipContent="Ranks highest-spend sub-categories and compares total amount vs deductible portion. This helps target both cost-control opportunities and tax-efficiency improvements."
            data={(data.byCategory?.tableData || [])
              .slice(0, 12)
              .map((row) => ({
                label: `${row.category} - ${row.subCategory}`,
                amount: row.amount || 0,
                deductible: row.deductible || 0,
              }))}
            xAxisKey="label"
            series={[
              {
                dataKey: "amount",
                label: "Amount",
                color: chartColors.warning,
              },
              {
                dataKey: "deductible",
                label: "Deductible",
                color: chartColors.info,
              },
            ]}
            height={360}
            yAxisLabel="Amount ($)"
            yAxisScaleType="linear"
            valueFormatter={formatCurrency}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.info, chartColors.warning, formatCurrency],
  );

  return (
    <>
      <DashboardReport
        title="Expense Summary & Allocation"
        description="Total expense overview with category/sub-category allocation and payment-method distribution."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />

      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const BUSINESS_FINANCIAL_REPORTS = {
  "expense-summary": ExpenseSummaryAllocation,
  "expense-trends": ExpenseTrends,
  "profit-loss": ProfitLossStatement,
};
